import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import html2canvas from 'html2canvas';
import QRCodeLib from 'qrcode';
import {
  Heart, Ticket, QrCode, Users, Copy, Check,
  Download, Share2, X, Sparkles, Landmark, ChevronLeft, Gift, BadgeCheck,
  Camera, MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMosque } from '../contexts/MosqueContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const rp = (a) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(a || 0);
const pct = (c, t) => Math.min((c / (t || 1)) * 100, 100);
const QUICK = [10000, 25000, 50000, 100000, 250000, 500000];

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'baru saja';
  if (s < 3600) return `${Math.floor(s / 60)} mnt lalu`;
  if (s < 86400) return `${Math.floor(s / 3600)} jam lalu`;
  return `${Math.floor(s / 86400)} hari lalu`;
};

const getInit = (name) => {
  if (!name || name === 'Hamba Allah') return '☽';
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
};

// ─── PROGRAM CARD (Clean White & Minimalist) ─────────────────────────────────

function ProgramCard({ p, idx, onDonate, onQR }) {
  const isUmroh = p.program_type === 'umroh';
  const isZakat = p.program_type === 'zakat';
  const percent = pct(p.current_amount, p.target_amount);
  const remaining = Math.max((p.target_amount || 0) - (p.current_amount || 0), 0);

  const handleShare = async () => {
    const appUrl = window.location.href;
    const ogUrl = `${API_URL}/api/infaq/${p.id}/og?app_url=${encodeURIComponent(appUrl)}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${p.title} — masjidberdaya.id`,
          text: p.description ? `${p.description.slice(0, 80)}...` : `Yuk infaq untuk program ${p.title}!`,
          url: ogUrl,
        });
      } catch (e) {
        if (e.name !== 'AbortError') {
          // Fallback: copy link
          try { await navigator.clipboard.writeText(ogUrl); toast.success('Link disalin!'); } catch {}
        }
      }
    } else {
      try { await navigator.clipboard.writeText(ogUrl); toast.success('Link program disalin!'); } catch {
        toast.error('Tidak dapat menyalin link');
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.07 * idx, type: 'spring', stiffness: 200, damping: 24 }}
      className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-lg transition-shadow border border-stone-200/70"
      data-testid={`infaq-card-${p.id}`}
    >
      {/* Full-width image at top — NO text overlay */}
      {p.image_url ? (
        <div className="w-full h-40 sm:h-48 overflow-hidden">
          <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
        </div>
      ) : isUmroh ? (
        <div className="w-full h-40 sm:h-48 bg-gradient-to-br from-[#3B2F0B] via-[#1E3A2F] to-[#022C22] flex items-center justify-center">
          <Sparkles className="w-14 h-14 text-amber-300/40" />
        </div>
      ) : (
        <div className="w-full h-40 sm:h-48 bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
          <Heart className="w-14 h-14 text-emerald-200" />
        </div>
      )}

      {/* White content area — below image */}
      <div className="px-4 py-4 sm:px-5 sm:py-5">
        <div className="flex items-center gap-2 mb-3">
          {isUmroh ? (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold">
              <Sparkles className="w-3 h-3" /> PREMIUM · UMROH
            </span>
          ) : isZakat ? (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">
              <Users className="w-3 h-3" /> ZAKAT FITRAH
            </span>
          ) : (
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
              {p.category || 'Infaq'}
            </span>
          )}
        </div>

        <h3 className="text-lg font-heading font-bold text-[#1C1917] mb-1.5">{p.title}</h3>
        <p className="text-sm font-body text-[#57534E] mb-4 line-clamp-2 leading-relaxed">{p.description}</p>

        {isUmroh && p.prize && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl bg-amber-50 border border-amber-100">
            <Ticket className="w-4 h-4 text-amber-600" />
            <span className="text-xs font-body text-amber-800">
              Hadiah: {p.prize} · {rp(p.ticket_price)}/tiket
            </span>
          </div>
        )}

        {/* Progress bar with % and remaining amount */}
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-[#A8A29E]">Terkumpul</span>
            <span className={`font-bold ${isUmroh ? 'text-amber-600' : isZakat ? 'text-emerald-700' : 'text-amber-600'}`}>
              {percent.toFixed(0)}%
            </span>
          </div>
          <div className="h-2.5 rounded-full overflow-hidden bg-stone-100">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${percent}%` }}
              transition={{ duration: 1.4, ease: [0.25, 1, 0.5, 1], delay: 0.12 + 0.07 * idx }}
              className={`h-full rounded-full ${isUmroh ? 'bg-gradient-to-r from-amber-400 to-amber-600' : isZakat ? 'bg-gradient-to-r from-emerald-400 to-emerald-600' : 'bg-gradient-to-r from-amber-400 to-yellow-500'}`}
            />
          </div>
          <div className="flex justify-between text-xs mt-1.5">
            <span className="font-bold text-[#022C22]">{rp(p.current_amount)}</span>
            <span className="text-[#A8A29E]">
              {remaining > 0 ? `Kurang ${rp(remaining)}` : `dari ${rp(p.target_amount)}`}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2.5">
          <button
            onClick={() => onQR(p)}
            className="w-11 h-11 rounded-full flex items-center justify-center transition-colors bg-stone-100 hover:bg-stone-200 text-stone-600"
            data-testid={`infaq-qr-btn-${p.id}`}
            title="Lihat QR Code"
          >
            <QrCode className="w-5 h-5" />
          </button>
          <button
            onClick={handleShare}
            className="w-11 h-11 rounded-full flex items-center justify-center transition-colors bg-emerald-50 hover:bg-emerald-100 text-emerald-700"
            data-testid={`infaq-share-btn-${p.id}`}
            title="Bagikan program"
          >
            <Share2 className="w-4.5 h-4.5" />
          </button>
          <button
            onClick={() => onDonate(p)}
            className={`flex-1 font-body font-semibold py-3 rounded-full flex items-center justify-center gap-2 transition-all active:scale-[0.97] ${
              isUmroh
                ? 'bg-amber-400 hover:bg-amber-300 text-emerald-950'
                : isZakat
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-emerald-700 hover:bg-emerald-800 text-white'
            }`}
            data-testid={`donate-btn-${p.id}`}
          >
            {isUmroh ? (
              <><Ticket className="w-4 h-4" /> Ambil Tiket</>
            ) : isZakat ? (
              <><Users className="w-4 h-4" /> Bayar Zakat</>
            ) : (
              <><Heart className="w-4 h-4" /> Donasi Sekarang</>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ─── QR MODAL ────────────────────────────────────────────────────────────────

function QRModal({ program: p, settings, onClose }) {
  const [copied, setCopied] = useState(false);
  const rek = p.rekening_info;
  const bnum = rek?.account_number || settings?.bank_account_number;
  const bname = rek?.bank_name || settings?.bank_name;
  const bholder = rek?.account_holder || settings?.bank_account_holder;

  const copy = () => {
    navigator.clipboard.writeText(bnum || '');
    setCopied(true);
    toast.success('Nomor rekening disalin!');
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="qr-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ type: 'spring', damping: 26, stiffness: 200 }}
        className="relative w-full max-w-sm bg-white rounded-t-3xl sm:rounded-3xl p-6 z-10"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center"
          data-testid="qr-modal-close"
        >
          <X className="w-5 h-5 text-stone-600" />
        </button>
        <h2 className="font-heading font-bold text-[#1C1917] text-xl mb-4 pr-10">{p.title}</h2>
        <img
          src={`${API_URL}/api/infaq/${p.id}/qr`}
          alt="QR Donasi"
          className="w-48 h-48 mx-auto rounded-2xl border border-stone-100 p-2 mb-4"
          data-testid="qr-image"
        />
        {bnum ? (
          <div className="bg-emerald-50 rounded-2xl p-4 text-sm font-body">
            <p className="flex items-center gap-2 text-emerald-800 font-semibold mb-2">
              <Landmark className="w-4 h-4" /> Rekening Tujuan
            </p>
            <p className="text-[#57534E]">{bname}</p>
            <div className="flex items-center justify-between mt-1.5">
              <span className="font-bold text-base text-[#022C22] tracking-wider">{bnum}</span>
              <button
                onClick={copy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold"
                data-testid="copy-btn-qr"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Tersalin' : 'Salin'}
              </button>
            </div>
            <p className="text-xs text-[#57534E] mt-1">a/n {bholder}</p>
          </div>
        ) : (
          <p className="text-center text-sm text-[#57534E]">Rekening belum diatur takmir.</p>
        )}
      </motion.div>
    </div>
  );
}

// ─── DONATION BOTTOM SHEET (4 steps for infaq, 3 for umroh/zakat) ────────────

function DonationSheet({ program: p, settings, feePerJiwa, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [selAmt, setSelAmt] = useState(null);
  const [custAmt, setCustAmt] = useState('');
  const [jiwa, setJiwa] = useState('');
  const [donorName, setDonorName] = useState('');
  const [isAnon, setIsAnon] = useState(false);
  const [payTab, setPayTab] = useState('qris');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const fileInputRef = useRef(null);

  const isUmroh = p.program_type === 'umroh';
  const isZakat = p.program_type === 'zakat';
  const isRegularInfaq = !isUmroh && !isZakat;

  const rek = p.rekening_info;
  const bnum = rek?.account_number || settings?.bank_account_number;
  const bname = rek?.bank_name || settings?.bank_name;
  const bholder = rek?.account_holder || settings?.bank_account_holder;
  const qrisImageUrl = settings?.qris_image_url;

  const actualAmt = selAmt || Number(custAmt) || 0;
  const zakatTotal = isZakat ? (Number(jiwa) || 0) * (p.nominal_per_jiwa || 0) : 0;
  const zakatFee = isZakat ? (Number(jiwa) || 0) * feePerJiwa : 0;
  const finalAmt = isZakat ? zakatTotal : actualAmt;
  const displayName = isAnon ? 'Hamba Allah' : donorName.trim() || 'Hamba Allah';

  const canNext1 = isZakat ? Number(jiwa) > 0 : finalAmt > 0;

  const STEPS = isRegularInfaq
    ? ['Nominal', 'Identitas', 'Metode', 'Konfirmasi']
    : ['Nominal', 'Identitas', 'Bayar'];

  const copyAcct = () => {
    navigator.clipboard.writeText(bnum || '');
    setCopied(true);
    toast.success('Nomor rekening disalin!');
    setTimeout(() => setCopied(false), 2500);
  };

  const handleProofChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('File harus berupa gambar'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Ukuran file maksimal 5MB'); return; }
    setProofFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setProofPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const downloadQris = () => {
    if (!qrisImageUrl) { toast.error('QRIS belum diatur takmir'); return; }
    window.open(qrisImageUrl, '_blank');
  };

  const submit = async () => {
    setBusy(true);
    try {
      let res;
      if (isUmroh) {
        res = await axios.post(
          `${API_URL}/api/infaq/${p.id}/ticket`,
          { amount: finalAmt, donor_name: displayName },
          { withCredentials: true }
        );
      } else if (isZakat) {
        res = await axios.post(
          `${API_URL}/api/zakat/${p.id}/pay`,
          { jiwa: Number(jiwa), donor_name: displayName },
          { withCredentials: true }
        );
      } else {
        // Regular infaq: use donate-with-proof
        const formData = new FormData();
        formData.append('amount', finalAmt);
        formData.append('donor_name', displayName);
        if (proofFile) formData.append('file', proofFile);
        res = await axios.post(`${API_URL}/api/infaq/${p.id}/donate-with-proof`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      const d = res.data;
      const displayAmt = isZakat ? (d.total || zakatTotal) : (d.amount || finalAmt);
      onSuccess({
        ...d,
        programTitle: p.title,
        donorName: displayName,
        amount: displayAmt,
        program_id: p.id,
        ticketCodes: d.ticket_codes || [],
      });
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error('Silakan masuk terlebih dahulu');
      } else {
        const det = e.response?.data?.detail;
        toast.error(typeof det === 'string' ? det : Array.isArray(det) ? 'Gagal memproses' : 'Gagal memproses');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center" data-testid="donation-sheet">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/50"
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 220 }}
        className="relative w-full max-w-md bg-white rounded-t-[2rem] z-10 overflow-y-auto pb-8"
        style={{ maxHeight: '90vh' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-stone-200 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-1 pb-4">
          <div>
            <p className="text-xs text-[#57534E] font-body mb-0.5">{p.title}</p>
            <h2 className="font-heading font-bold text-[#022C22] text-xl leading-tight">
              {isUmroh ? 'Ambil Tiket Undian' : isZakat ? 'Bayar Zakat Fitrah' : 'Donasi Sekarang'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center mt-1 flex-shrink-0"
            data-testid="sheet-close"
          >
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6 px-6">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-1.5">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    step > i + 1
                      ? 'bg-emerald-600 text-white'
                      : step === i + 1
                      ? 'bg-[#022C22] text-white'
                      : 'bg-stone-100 text-stone-400'
                  }`}
                >
                  {step > i + 1 ? '✓' : i + 1}
                </div>
                <span
                  className={`text-xs font-body ${
                    step === i + 1 ? 'text-[#022C22] font-semibold' : 'text-stone-400'
                  }`}
                >
                  {s}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-px max-w-6 ${step > i + 1 ? 'bg-emerald-500' : 'bg-stone-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step content */}
        <div className="px-6">
          <AnimatePresence mode="wait">
            {/* ── STEP 1: Nominal ── */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                className="space-y-4"
              >
                {isZakat ? (
                  <div className="bg-emerald-50 rounded-2xl p-4 font-body space-y-3">
                    <p className="text-emerald-700 text-sm">
                      Zakat fitrah{' '}
                      <span className="font-bold">{rp(p.nominal_per_jiwa)}/jiwa</span>
                      {feePerJiwa > 0 && ` + biaya layanan ${rp(feePerJiwa)}/jiwa`}
                    </p>
                    <input
                      data-testid="sheet-jiwa-input"
                      type="number"
                      min="1"
                      placeholder="Masukkan jumlah jiwa"
                      className="w-full bg-white border border-emerald-200 rounded-xl px-4 py-3.5 text-lg font-semibold text-[#022C22] outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-400"
                      value={jiwa}
                      onChange={(e) => setJiwa(e.target.value)}
                    />
                    {Number(jiwa) > 0 && (
                      <div className="border-t border-emerald-100 pt-3 space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-emerald-700">Zakat ({jiwa} jiwa)</span>
                          <span className="font-semibold">{rp(zakatTotal)}</span>
                        </div>
                        {zakatFee > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-emerald-700">Biaya layanan</span>
                            <span className="font-semibold">{rp(zakatFee)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-base pt-1 border-t border-emerald-100">
                          <span className="text-[#022C22]">Total</span>
                          <span className="text-emerald-700">{rp(zakatTotal)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2.5">
                      {QUICK.map((amt) => (
                        <button
                          key={amt}
                          onClick={() => { setSelAmt(amt); setCustAmt(''); }}
                          className={`py-3 rounded-2xl text-sm font-semibold font-body border transition-all ${
                            selAmt === amt
                              ? 'bg-[#022C22] text-white border-[#022C22] scale-95'
                              : 'bg-stone-50 text-[#1C1917] border-stone-200 hover:border-emerald-300 hover:bg-emerald-50'
                          }`}
                          data-testid={`quick-amount-${amt}`}
                        >
                          {amt >= 1000000 ? `Rp ${amt / 1000000}jt` : `Rp ${amt / 1000}rb`}
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 font-body text-sm select-none">Rp</span>
                      <input
                        data-testid="sheet-custom-amount"
                        type="number"
                        placeholder="Nominal lainnya"
                        className="w-full pl-10 pr-4 py-3.5 rounded-2xl border border-stone-200 bg-stone-50 text-[#1C1917] font-body text-sm outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
                        value={custAmt}
                        onChange={(e) => { setCustAmt(e.target.value); setSelAmt(null); }}
                      />
                    </div>
                    {isUmroh && finalAmt > 0 && (p.ticket_price || 0) > 0 && (
                      <div className="bg-amber-50 rounded-xl px-3 py-2.5 text-xs font-body text-amber-800">
                        ≈ {Math.max(Math.floor(finalAmt / p.ticket_price), 1)} tiket undian{p.prize ? ` ${p.prize}` : ''}
                      </div>
                    )}
                  </>
                )}

                <button
                  onClick={() => setStep(2)}
                  disabled={!canNext1}
                  className="w-full bg-[#022C22] disabled:opacity-40 text-white font-semibold py-4 rounded-2xl flex items-center justify-center gap-2 mt-1 transition-opacity"
                  data-testid="sheet-next-1"
                >
                  Selanjutnya <span>→</span>
                </button>
              </motion.div>
            )}

            {/* ── STEP 2: Identitas ── */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                className="space-y-4"
              >
                <input
                  data-testid="sheet-donor-name"
                  type="text"
                  placeholder="Nama Anda (opsional)"
                  className="w-full px-4 py-3.5 rounded-2xl border border-stone-200 bg-stone-50 text-[#1C1917] font-body text-sm outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  disabled={isAnon}
                />

                <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="sheet-anon-toggle">
                  <button
                    type="button"
                    onClick={() => setIsAnon(!isAnon)}
                    className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ${isAnon ? 'bg-emerald-600' : 'bg-stone-200'}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${isAnon ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                  <span className="text-sm font-body text-[#57534E]">
                    Donasi sebagai <span className="font-semibold text-[#1C1917]">"Hamba Allah"</span>
                  </span>
                </label>

                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => setStep(1)}
                    className="flex items-center gap-1.5 px-5 py-3.5 rounded-2xl border border-stone-200 text-stone-600 font-body text-sm"
                    data-testid="sheet-back-2"
                  >
                    <ChevronLeft className="w-4 h-4" /> Kembali
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    className="flex-1 bg-[#022C22] text-white font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2"
                    data-testid="sheet-next-2"
                  >
                    Selanjutnya <span>→</span>
                  </button>
                </div>
              </motion.div>
            )}

            {/* ── STEP 3: Metode Pembayaran ── */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                className="space-y-4"
              >
                {/* Tabs */}
                <div className="flex bg-stone-100 rounded-2xl p-1">
                  {['qris', 'transfer'].map((t) => (
                    <button
                      key={t}
                      onClick={() => setPayTab(t)}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-semibold font-body transition-all ${
                        payTab === t ? 'bg-white text-[#022C22] shadow-sm' : 'text-stone-500'
                      }`}
                      data-testid={`tab-${t}`}
                    >
                      {t === 'qris' ? 'QRIS' : 'Transfer Bank'}
                    </button>
                  ))}
                </div>

                {payTab === 'qris' && (
                  <div className="text-center py-2">
                    {qrisImageUrl ? (
                      <>
                        <img
                          src={qrisImageUrl}
                          alt="QRIS Masjid"
                          className="w-44 h-44 mx-auto rounded-2xl border border-stone-100 object-contain p-2 bg-white"
                          data-testid="sheet-qr-img"
                        />
                        <button
                          onClick={downloadQris}
                          className="mt-3 flex items-center gap-2 mx-auto px-4 py-2 rounded-full bg-emerald-50 text-emerald-700 text-sm font-semibold border border-emerald-100 hover:bg-emerald-100 transition-colors"
                          data-testid="download-qris-btn"
                        >
                          <Download className="w-4 h-4" /> Unduh QRIS
                        </button>
                      </>
                    ) : (
                      <div className="py-8 text-center">
                        <QrCode className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                        <p className="text-sm text-[#57534E]">QRIS belum diatur takmir.</p>
                        <p className="text-xs text-stone-400 mt-1">Gunakan Transfer Bank.</p>
                      </div>
                    )}
                    <p className="text-xs text-[#57534E] font-body mt-2">
                      Scan dengan aplikasi e-wallet atau mobile banking
                    </p>
                  </div>
                )}

                {payTab === 'transfer' && (
                  <div className="bg-emerald-50 rounded-2xl p-4 font-body">
                    {bnum ? (
                      <>
                        <p className="flex items-center gap-2 text-emerald-800 font-semibold text-sm mb-3">
                          <Landmark className="w-4 h-4" /> {bname}
                        </p>
                        <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-emerald-100">
                          <span className="font-bold text-lg tracking-widest text-[#022C22]">{bnum}</span>
                          <button
                            onClick={copyAcct}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold ml-2 flex-shrink-0"
                            data-testid="copy-account-btn"
                          >
                            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            {copied ? 'Tersalin!' : 'Salin'}
                          </button>
                        </div>
                        <p className="text-xs text-[#57534E] mt-2">
                          a/n <span className="font-semibold text-[#1C1917]">{bholder}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-[#57534E] text-center py-4">Rekening belum diatur oleh takmir.</p>
                    )}
                  </div>
                )}

                {/* Summary */}
                <div className="bg-stone-50 rounded-2xl px-4 py-3.5 flex justify-between items-center border border-stone-100">
                  <span className="text-sm text-[#57534E] font-body">Total Donasi</span>
                  <span className="text-xl font-bold text-[#022C22] font-heading">{rp(finalAmt)}</span>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(2)}
                    className="flex items-center gap-1.5 px-5 py-3.5 rounded-2xl border border-stone-200 text-stone-600 font-body text-sm"
                    data-testid="sheet-back-3"
                  >
                    <ChevronLeft className="w-4 h-4" /> Kembali
                  </button>
                  {isRegularInfaq ? (
                    <button
                      onClick={() => setStep(4)}
                      className="flex-1 bg-[#022C22] text-white font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2"
                      data-testid="sheet-next-3"
                    >
                      Lanjut <span>→</span>
                    </button>
                  ) : (
                    <button
                      onClick={submit}
                      disabled={busy}
                      className="flex-1 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition-colors"
                      data-testid="sheet-confirm-btn"
                    >
                      {busy ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          Memproses...
                        </span>
                      ) : (
                        '✓ Konfirmasi Sudah Bayar'
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── STEP 4: Upload Bukti (infaq only) ── */}
            {step === 4 && isRegularInfaq && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                className="space-y-4"
              >
                <div className="text-center mb-2">
                  <p className="text-sm font-body text-[#57534E]">Upload foto bukti pembayaran (QRIS / Transfer)</p>
                  <p className="text-xs text-stone-400 mt-0.5">Wajib sebelum menyelesaikan donasi</p>
                </div>

                {proofPreview ? (
                  <div className="relative rounded-2xl overflow-hidden">
                    <img src={proofPreview} alt="Bukti Transfer" className="w-full h-48 object-cover rounded-2xl" />
                    <button
                      onClick={() => { setProofFile(null); setProofPreview(null); }}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white"
                      data-testid="remove-proof-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-2 left-2 right-2 bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5 w-fit mx-auto">
                      <Check className="w-3.5 h-3.5" /> Bukti terpilih
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-44 border-2 border-dashed border-stone-300 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-all"
                    data-testid="proof-upload-area"
                  >
                    <Camera className="w-10 h-10 text-stone-300 mb-2" />
                    <p className="text-sm font-body font-medium text-stone-500">Klik untuk upload foto bukti</p>
                    <p className="text-xs text-stone-400 mt-1">Screenshot transfer atau struk QRIS</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleProofChange}
                      data-testid="proof-file-input"
                    />
                  </div>
                )}

                {/* Summary */}
                <div className="bg-stone-50 rounded-2xl px-4 py-3.5 border border-stone-100">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-[#57534E] font-body">Total Donasi</span>
                    <span className="text-xl font-bold text-[#022C22] font-heading">{rp(finalAmt)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-stone-400">Atas Nama</span>
                    <span className="text-xs font-semibold text-[#1C1917]">{displayName}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(3)}
                    className="flex items-center gap-1.5 px-5 py-3.5 rounded-2xl border border-stone-200 text-stone-600 font-body text-sm"
                    data-testid="sheet-back-4"
                  >
                    <ChevronLeft className="w-4 h-4" /> Kembali
                  </button>
                  <button
                    onClick={submit}
                    disabled={!proofFile || busy}
                    className="flex-1 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition-colors"
                    data-testid="sheet-confirm-btn"
                  >
                    {busy ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        Memproses...
                      </span>
                    ) : (
                      '✓ Konfirmasi Donasi'
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

// ─── BUKTI INFAQ DIGITAL MODAL ────────────────────────────────────────────────

function EKuitansi({ receipt, mosqueSettings, onClose, onWall }) {
  const receiptRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [sharingImg, setSharingImg] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [logoError, setLogoError] = useState(false);

  // Generate reference number: INF-YYYYMMDD-LAST5
  const dateStr = receipt.created_at
    ? new Date(receipt.created_at).toISOString().slice(0, 10).replace(/-/g, '')
    : new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const isZakat = !!(receipt.jiwa);
  const isUmroh = !!(receipt.ticketCodes?.length > 0);
  const prefix = isZakat ? 'ZKT' : isUmroh ? 'UMR' : 'INF';
  const refNum = `${prefix}-${dateStr}-${(receipt.id || '').slice(-5).toUpperCase() || 'XXXXX'}`;
  const verifyUrl = receipt.id ? `${API_URL}/api/infaq/receipt/${receipt.id}` : '';

  // Pre-generate QR code as data URL (compatible with html2canvas)
  useEffect(() => {
    if (!verifyUrl) return;
    QRCodeLib.toDataURL(verifyUrl, {
      width: 110, margin: 1,
      color: { dark: '#022C22', light: '#FFFFFF' },
    }).then(setQrDataUrl).catch(() => {});
  }, [verifyUrl]);

  const captureReceipt = () =>
    html2canvas(receiptRef.current, {
      scale: 2.5,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      logging: false,
    });

  const download = async () => {
    if (!receiptRef.current) return;
    setDownloading(true);
    try {
      const canvas = await captureReceipt();
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      const slug = (receipt.programTitle || 'infaq').replace(/\s+/g, '-').toLowerCase();
      a.download = `bukti-infaq-${slug}-${Date.now()}.png`;
      a.click();
    } catch {
      toast.error('Gagal mengunduh bukti');
    }
    setDownloading(false);
  };

  const shareWAImage = async () => {
    if (!receiptRef.current) return;
    setSharingImg(true);
    try {
      const canvas = await captureReceipt();
      const slug = (receipt.programTitle || 'infaq').replace(/\s+/g, '-').toLowerCase();
      const fileName = `bukti-infaq-${slug}.png`;

      // Try Web Share API (mobile browsers)
      if (navigator.share) {
        canvas.toBlob(async (blob) => {
          const file = new File([blob], fileName, { type: 'image/png' });
          const canShareFiles = navigator.canShare && navigator.canShare({ files: [file] });
          if (canShareFiles) {
            await navigator.share({ files: [file], title: 'Bukti Infaq Digital' });
          } else {
            // Fallback: open WA with text
            const name = mosqueSettings?.mosque_name || 'Masjid';
            const msg = `*Alhamdulillah, Bukti Infaq Saya!*\n\nProgram: ${receipt.programTitle}\nMasjid: ${name}\nJumlah: ${rp(receipt.amount)}\nAtas Nama: ${receipt.donorName}\n\nNo. Ref: ${refNum}`;
            window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
          }
          setSharingImg(false);
        });
      } else {
        // Desktop: download image + hint to share
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = fileName;
        a.click();
        toast.success('Gambar resi diunduh! Silakan kirim ke WhatsApp');
        setSharingImg(false);
      }
    } catch {
      toast.error('Gagal membuat gambar resi');
      setSharingImg(false);
    }
  };

  const mosqueName = mosqueSettings?.mosque_name || 'Masjid';
  const mosqueAddress = mosqueSettings?.location_address || '';
  const logoUrl = mosqueSettings?.logo_url;
  const verified = receipt.is_verified;

  // Detail rows
  const rows = [
    { label: 'Atas Nama', value: receipt.donorName || 'Hamba Allah' },
    { label: 'Program', value: receipt.programTitle || '-' },
    isZakat && { label: 'Jumlah Jiwa', value: `${receipt.jiwa} jiwa` },
    { label: 'Tanggal', value: fmtDate(receipt.created_at) },
    isUmroh && receipt.ticketCodes?.length > 0 && { label: 'Kode Tiket', value: receipt.ticketCodes.join(', ') },
  ].filter(Boolean);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="ekuitansi-modal">
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />
      <motion.div
        initial={{ scale: 0.88, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.88, opacity: 0, y: 20 }}
        transition={{ type: 'spring', damping: 22, stiffness: 280 }}
        className="relative w-full max-w-sm z-10 overflow-y-auto"
        style={{ maxHeight: '92vh' }}
      >
        {/* ── Printable receipt ── */}
        <div
          ref={receiptRef}
          style={{
            fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            background: '#ffffff',
            borderRadius: 20,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          }}
        >
          {/* ── Header: Logo (center) + Mosque Name + Address ── */}
          <div style={{ padding: '28px 24px 20px', textAlign: 'center', borderBottom: '2px dashed #D4D4D0' }}>
            {logoUrl && !logoError ? (
              <img
                src={logoUrl}
                alt="Logo Masjid"
                crossOrigin="anonymous"
                onError={() => setLogoError(true)}
                style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 12px', display: 'block', border: '3px solid #D4D4D0' }}
              />
            ) : (
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#ECFDF5', margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #D1FAE5', fontSize: 30 }}>
                ☽
              </div>
            )}
            <p style={{ fontSize: 18, fontWeight: 800, color: '#022C22', margin: '0 0 4px', letterSpacing: -0.3 }}>
              {mosqueName}
            </p>
            {mosqueAddress && (
              <p style={{ fontSize: 11, color: '#78716C', margin: '0 0 12px', lineHeight: 1.5 }}>
                {mosqueAddress}
              </p>
            )}
            <div style={{ display: 'inline-block', background: '#ECFDF5', borderRadius: 8, padding: '5px 14px', border: '1px solid #A7F3D0' }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: '#065F46', letterSpacing: 2, textTransform: 'uppercase' }}>
                BUKTI INFAQ DIGITAL
              </span>
            </div>
          </div>

          {/* ── Reference Number ── */}
          <div style={{ padding: '14px 24px 0', textAlign: 'center' }}>
            <p style={{ fontSize: 10, color: '#A8A29E', margin: '0 0 3px', textTransform: 'uppercase', letterSpacing: 1 }}>No. Referensi</p>
            <p style={{ fontFamily: "'Courier New', 'Menlo', 'Consolas', monospace", fontSize: 15, color: '#022C22', fontWeight: 700, margin: 0, letterSpacing: 2.5 }}>
              {refNum}
            </p>
          </div>

          {/* ── Amount ── */}
          <div style={{ padding: '14px 24px 16px', textAlign: 'center', borderBottom: '2px dashed #D4D4D0' }}>
            <p style={{ fontSize: 10, color: '#78716C', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: 1 }}>Jumlah Donasi</p>
            <p style={{ fontFamily: "'Courier New', 'Menlo', 'Consolas', monospace", fontSize: 32, fontWeight: 800, color: '#022C22', margin: '0 0 10px', letterSpacing: -0.5 }}>
              {rp(receipt.amount)}
            </p>
            {/* Status badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: verified ? '#ECFDF5' : '#FEF9C3',
              borderRadius: 20, padding: '5px 13px',
              border: `1px solid ${verified ? '#A7F3D0' : '#FEF08A'}`,
            }}>
              <span style={{ fontSize: 14 }}>{verified ? '✓' : '⏳'}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: verified ? '#065F46' : '#854D0E', letterSpacing: 0.5 }}>
                {verified ? 'TERVERIFIKASI' : 'MENUNGGU VERIFIKASI'}
              </span>
            </div>
          </div>

          {/* ── Detail Rows ── */}
          <div style={{ padding: '16px 24px', borderBottom: '2px dashed #D4D4D0' }}>
            {rows.map((row) => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: '#A8A29E', flexShrink: 0, minWidth: 90 }}>{row.label}</span>
                <span style={{ fontFamily: "'Courier New', monospace", fontSize: 12, fontWeight: 700, color: '#1C1917', textAlign: 'right', marginLeft: 8, maxWidth: '58%', wordBreak: 'break-word' }}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>

          {/* ── QR Code ── */}
          {qrDataUrl && (
            <div style={{ padding: '16px 24px', textAlign: 'center', borderBottom: '2px dashed #D4D4D0' }}>
              <img
                src={qrDataUrl}
                alt="QR Verifikasi"
                style={{ width: 88, height: 88, margin: '0 auto', display: 'block' }}
              />
              <p style={{ fontSize: 10, color: '#A8A29E', margin: '6px 0 0' }}>Scan untuk verifikasi keabsahan</p>
            </div>
          )}

          {/* ── Doa ── */}
          <div style={{ background: '#FFFBEB', padding: '14px 24px', borderBottom: '1px solid #FEF3C7' }}>
            <p style={{ fontSize: 11, color: '#92400E', fontStyle: 'italic', margin: 0, lineHeight: 1.7, textAlign: 'center' }}>
              "Semoga Allah melipatgandakan kebaikan Anda dan menjadikannya amal yang diterima di sisi-Nya. Aamiin."
            </p>
          </div>

          {/* ── Footer ── */}
          <div style={{ background: '#F5F5F4', padding: '10px 24px', textAlign: 'center' }}>
            <p style={{ fontFamily: "'Courier New', monospace", fontSize: 10, color: '#A8A29E', margin: 0 }}>
              masjidberdaya.id · Platform Masjid Digital Indonesia
            </p>
          </div>
        </div>

        {/* ── Action Buttons ── */}
        <div className="mt-4 space-y-3">
          <button
            onClick={download}
            disabled={downloading}
            className="w-full bg-[#022C22] hover:bg-[#033a2e] text-white font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            data-testid="download-receipt-btn"
          >
            {downloading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Mengunduh...
              </span>
            ) : (
              <><Download className="w-5 h-5" /> Unduh Bukti Infaq</>
            )}
          </button>

          {(() => {
            const waNum = (mosqueSettings?.whatsapp_number || '').replace(/\D/g, '');
            const msg = `Assalamualaikum, saya ${receipt.donorName} baru saja melakukan donasi sebesar ${rp(receipt.amount)} untuk program ${receipt.programTitle}. No. Ref: ${refNum}. Mohon verifikasinya.`;
            return waNum ? (
              <a
                href={`https://wa.me/${waNum}?text=${encodeURIComponent(msg)}`}
                target="_blank"
                rel="noreferrer"
                className="w-full font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition-colors"
                style={{ background: '#25D366', color: '#ffffff', display: 'flex' }}
                data-testid="confirm-wa-takmir-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Konfirmasi ke WhatsApp Takmir
              </a>
            ) : null;
          })()}

          <button
            onClick={shareWAImage}
            disabled={sharingImg}
            className="w-full font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition-colors border border-stone-200 text-stone-700 bg-white disabled:opacity-60"
            data-testid="share-wa-btn"
          >
            {sharingImg ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-stone-400 border-t-stone-700 rounded-full animate-spin" />
                Menyiapkan gambar...
              </span>
            ) : (
              <><Share2 className="w-5 h-5" /> Bagikan Gambar ke WhatsApp</>
            )}
          </button>

          <button
            onClick={onWall}
            className="w-full border border-emerald-200 bg-emerald-50 text-emerald-800 font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 text-sm transition-colors hover:bg-emerald-100"
            data-testid="view-wall-btn"
          >
            <Gift className="w-4 h-4" />
            Lihat Nama Saya di Daftar Donatur
          </button>

          <button onClick={onClose} className="w-full text-stone-400 font-body text-sm py-2">
            Tutup
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── DONOR WALL INLINE (auto-refresh) ─────────────────────────────────────────

function DonorWall({ programId, onViewAll }) {
  const [donors, setDonors] = useState([]);

  useEffect(() => {
    const load = () =>
      axios
        .get(`${API_URL}/api/infaq/${programId}/donors`)
        .then((r) => setDonors(r.data))
        .catch(() => {});
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [programId]);

  if (donors.length === 0) return null;

  return (
    <div
      className="bg-white rounded-3xl border border-stone-100 px-5 py-4 shadow-sm"
      data-testid={`wall-inline-${programId}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-heading font-semibold text-[#1C1917]">Daftar Donatur</span>
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" title="Live" />
        </div>
        <button
          onClick={onViewAll}
          className="text-xs text-emerald-700 font-medium hover:underline"
          data-testid={`wall-view-all-${programId}`}
        >
          Lihat Semua
        </button>
      </div>
      <div className="space-y-3">
        {donors.slice(0, 5).map((d, i) => (
          <motion.div
            key={`${d.donor_name}-${i}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.04 * i }}
            className="flex items-center gap-3"
            data-testid={`wall-donor-inline-${i}`}
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-700 to-[#022C22] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {getInit(d.donor_name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-sm text-[#1C1917] truncate">
                  {d.donor_name || 'Hamba Allah'}
                </span>
                {d.is_verified && (
                  <BadgeCheck className="w-4 h-4 text-amber-500 flex-shrink-0" title="Terverifikasi Admin" />
                )}
              </div>
              <p className="text-xs text-[#A8A29E]">{timeAgo(d.created_at)}</p>
            </div>
            <span className="text-sm font-bold text-emerald-700 flex-shrink-0">{rp(d.amount)}</span>
          </motion.div>
        ))}
      </div>
      {donors.length > 5 && (
        <button
          onClick={onViewAll}
          className="w-full mt-3 pt-3 text-xs font-body text-[#57534E] border-t border-stone-100 text-center hover:text-emerald-700 transition-colors"
          data-testid={`wall-more-${programId}`}
        >
          +{donors.length - 5} donatur lainnya →
        </button>
      )}
    </div>
  );
}

// ─── WALL OF KINDNESS MODAL ───────────────────────────────────────────────────

function WallModal({ programId, programTitle, onClose }) {
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/infaq/${programId}/donors`)
      .then((r) => setDonors(r.data))
      .catch(() => setDonors([]))
      .finally(() => setLoading(false));
  }, [programId]);

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center" data-testid="wall-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 220 }}
        className="relative w-full max-w-md bg-white rounded-t-[2rem] z-10 pb-8"
        style={{ maxHeight: '80vh' }}
      >
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-stone-200 rounded-full" />
        </div>
        <div className="flex items-center justify-between px-6 pt-1 pb-4">
          <div>
            <p className="text-xs text-[#57534E] font-body">Transparansi Donasi</p>
            <h2 className="font-heading font-bold text-[#022C22] text-xl">Daftar Donatur</h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center"
            data-testid="wall-close"
          >
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>

        <div className="px-6 overflow-y-auto" style={{ maxHeight: '55vh' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : donors.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="w-12 h-12 text-stone-200 mx-auto mb-3" />
              <p className="text-[#57534E] font-body text-sm">Jadilah yang pertama berdonasi!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {donors.map((d, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i }}
                  className="flex items-center gap-3 p-3.5 rounded-2xl bg-stone-50 border border-stone-100"
                  data-testid={`wall-donor-${i}`}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-700 to-[#022C22] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {getInit(d.donor_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <p className="font-semibold text-sm text-[#1C1917] truncate">{d.donor_name || 'Hamba Allah'}</p>
                      {d.is_verified && (
                        <BadgeCheck className="w-4 h-4 text-amber-500 flex-shrink-0" title="Terverifikasi Admin" />
                      )}
                    </div>
                    <p className="text-xs text-[#A8A29E]">{timeAgo(d.created_at)}</p>
                  </div>
                  <span className="text-sm font-bold text-emerald-700 flex-shrink-0">{rp(d.amount)}</span>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

const InfaqPage = () => {
  const { mosqueId } = useMosque();
  const [programs, setPrograms] = useState([]);
  const [settings, setSettings] = useState(null);
  const [feePerJiwa, setFeePerJiwa] = useState(1000);
  const [qrProgram, setQrProgram] = useState(null);
  const [sheetProgram, setSheetProgram] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [wallProgram, setWallProgram] = useState(null);

  const reload = () =>
    axios
      .get(`${API_URL}/api/infaq`, { params: { mosque_id: mosqueId } })
      .then((r) => setPrograms(r.data))
      .catch(() => setPrograms([]));

  useEffect(() => {
    reload();
    axios.get(`${API_URL}/api/settings`, { params: { mosque_id: mosqueId } }).then((r) => setSettings(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/platform-settings`).then((r) => setFeePerJiwa(r.data.ps_zakat_fee_per_jiwa || 1000)).catch(() => {});
  }, [mosqueId]);

  const handleSheetSuccess = (data) => {
    setSheetProgram(null);
    setReceipt(data);
    reload();
  };

  const handleWall = () => {
    const pid = receipt?.program_id;
    const ptitle = receipt?.programTitle;
    setReceipt(null);
    if (pid) setWallProgram({ id: pid, title: ptitle });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-[#F8F9FA] px-4 py-8 pb-28">
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold tracking-tight text-primary-dark mb-2">Donasi & Infaq</h1>
        <p className="text-sm font-body text-text-secondary">Mari berbagi keberkahan bersama jamaah</p>
      </div>

      <div className="space-y-5">
        {programs.map((p, idx) => (
          <div key={p.id} className="space-y-2">
            <ProgramCard
              p={p}
              idx={idx}
              onDonate={(prog) => setSheetProgram(prog)}
              onQR={(prog) => setQrProgram(prog)}
            />
            <DonorWall
              programId={p.id}
              onViewAll={() => setWallProgram({ id: p.id, title: p.title })}
            />
          </div>
        ))}
        {programs.length === 0 && (
          <div className="bg-white rounded-3xl border border-stone-100 p-12 text-center">
            <Heart className="w-14 h-14 text-stone-300 mx-auto mb-4" />
            <p className="text-text-secondary font-body">Belum ada program donasi aktif</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {qrProgram && (
          <QRModal program={qrProgram} settings={settings} onClose={() => setQrProgram(null)} />
        )}
        {sheetProgram && (
          <DonationSheet
            program={sheetProgram}
            settings={settings}
            feePerJiwa={feePerJiwa}
            onClose={() => setSheetProgram(null)}
            onSuccess={handleSheetSuccess}
          />
        )}
        {receipt && (
          <EKuitansi
            receipt={receipt}
            mosqueSettings={settings}
            onClose={() => setReceipt(null)}
            onWall={handleWall}
          />
        )}
        {wallProgram && (
          <WallModal
            programId={wallProgram.id}
            programTitle={wallProgram.title}
            onClose={() => setWallProgram(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default InfaqPage;
